import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { cn } from '../utils/cn';

export interface BreadcrumbCrumb {
  /** Display text. */
  label: string;
  /** Optional href — when omitted the crumb renders as static text
   *  (typically the trailing current-page crumb). */
  to?: string;
}

export interface BreadcrumbProps {
  /** Ordered list of crumbs, leftmost first. The component always
   *  prepends a "Home" crumb pointing to `/`, so callers should
   *  start at the section level (e.g. `[{label:'Shop',to:'/shop'},…]`).
   */
  items: BreadcrumbCrumb[];
  className?: string;
  /** Override the "Home" label / href if needed. */
  homeLabel?: string;
  homeTo?: string;
}

/**
 * Storefront breadcrumb — renders `Home › <Section> › <Page>` with the
 * existing muted separator color. Used on the shop list (when a
 * category filter is active) and on every PDP. Behaviour per Master
 * Prompt v2.0 Fix #9.
 */
export function Breadcrumb({
  items,
  className,
  homeLabel = 'Home',
  homeTo = '/',
}: BreadcrumbProps) {
  const crumbs: BreadcrumbCrumb[] = [{ label: homeLabel, to: homeTo }, ...items];

  return (
    <nav
      aria-label="Breadcrumb"
      className={cn(
        'flex flex-wrap items-center gap-1 text-xs text-[var(--color-text-secondary)] sm:text-sm',
        className,
      )}
    >
      <ol className="flex flex-wrap items-center gap-1">
        {crumbs.map((c, i) => {
          const isLast = i === crumbs.length - 1;
          return (
            <li key={`${c.label}-${i}`} className="flex items-center gap-1">
              {i > 0 && (
                <ChevronRight
                  className="h-3 w-3 shrink-0 text-[var(--color-text-muted)]"
                  aria-hidden="true"
                />
              )}
              {isLast || !c.to ? (
                <span
                  aria-current={isLast ? 'page' : undefined}
                  className={cn(
                    isLast
                      ? 'font-medium text-[var(--color-text-primary)]'
                      : 'text-[var(--color-text-secondary)]',
                  )}
                >
                  {c.label}
                </span>
              ) : (
                <Link
                  to={c.to}
                  className="text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:underline"
                >
                  {c.label}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

export default Breadcrumb;
